/**
 * One customer for support: where they can be reached, what was sent to them and why something
 * did not arrive. Erasure lives in a danger zone at the bottom.
 */
import type { AdminCustomer, Channel, Subscription } from "@aiyo/shared";
import { Bell, Envelope, PaperPlane, Smartphone, TrashBin } from "@gravity-ui/icons";
import { Button, Chip, toast } from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { DeliveryDrawer } from "../components/DeliveryDrawer";
import { DeliveryTable } from "../components/DeliveryTable";
import { ConfirmDialog } from "../components/dialogs";
import {
  ChannelChip,
  Code,
  DangerZone,
  ErrorBlock,
  KeyValue,
  LoadingBlock,
  Page,
  PageHeader,
  Panel,
  StateChip,
} from "../components/ui";
import { useFormat } from "../lib/format";
import { useCustomer, useDeliveryLog, useEraseCustomer, useMe, useUpdateSubscription } from "../lib/queries";

export function CustomerDetailPage() {
  const { customerId = "" } = useParams();
  const { t } = useTranslation();
  const customer = useCustomer(customerId);

  if (customer.isPending) return <LoadingBlock />;
  if (customer.isError) return <ErrorBlock error={customer.error} onRetry={() => customer.refetch()} />;
  return <CustomerView c={customer.data} breadcrumb={t("nav.customers")} />;
}

function CustomerView({ c, breadcrumb }: { c: AdminCustomer; breadcrumb: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const canEdit = me?.role !== "viewer";
  const name = typeof c.attributes.name === "string" && c.attributes.name ? c.attributes.name : null;
  const [params, setParams] = useSearchParams();
  const openId = params.get("delivery");
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("delivery", id);
    else next.delete("delivery");
    setParams(next, { replace: true });
  };

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <Link to="/customers" className="hover:text-foreground">
            {breadcrumb}
          </Link>
        }
        title={name ?? c.external_id}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Code>{c.external_id}</Code>
            <span>{c.app.name}</span>
            <span>
              {t("customers.detail.registered")} {fmt.relative(c.created_at)}
            </span>
          </span>
        }
        actions={
          canEdit ? (
            <Button
              onPress={() => navigate(`/send?app=${c.app.id}&customer=${encodeURIComponent(c.external_id)}`)}
            >
              <PaperPlane />
              {t("customers.detail.sendTest")}
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <Panel title={t("customers.detail.reach")} description={t("customers.detail.reachHint")}>
            <Addresses customer={c} canEdit={canEdit} />
          </Panel>

          <Panel title={t("customers.detail.history")} description={t("customers.detail.historyHint")}>
            <History customerId={c.id} onOpen={setOpen} />
          </Panel>
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <Panel title={t("customers.detail.activity", { days: c.activity.days })}>
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label={t("overview.kpi.deliveries")} value={c.activity.total} />
              <MiniStat label={t("overview.kpi.reached")} value={c.activity.delivered} />
              <MiniStat
                label={t("overview.kpi.problems")}
                value={c.activity.problems}
                tone={c.activity.problems ? "danger" : undefined}
              />
            </div>
            {c.activity.last_delivery_at ? (
              <p className="text-xs text-muted">
                {t("customers.detail.lastDelivery", { when: fmt.relative(c.activity.last_delivery_at) })}
              </p>
            ) : null}
          </Panel>

          <Panel title={t("customers.detail.profile")}>
            <Profile c={c} />
          </Panel>

          {canEdit ? <EraseCustomer c={c} /> : null}
        </div>
      </div>

      <DeliveryDrawer deliveryId={openId} onClose={() => setOpen(null)} />
    </Page>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: number; tone?: "danger" }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-surface-secondary p-3">
      <span className="text-xs text-muted">{label}</span>
      <span className={`text-lg font-semibold ${tone === "danger" ? "text-danger" : ""}`}>{value}</span>
    </div>
  );
}

const CHANNEL_ICON: Record<Channel, typeof Bell> = {
  fcm: Smartphone,
  apns: Smartphone,
  webpush: Bell,
  email: Envelope,
  sms: Smartphone,
  whatsapp: Smartphone,
};

function Addresses({ customer, canEdit }: { customer: AdminCustomer; canEdit: boolean }) {
  const { t } = useTranslation();
  if (customer.subscriptions.length === 0) {
    return <p className="text-sm text-muted">{t("customers.detail.noSubscriptions")}</p>;
  }
  return (
    <ul className="-my-1 flex flex-col divide-y divide-separator">
      {customer.subscriptions.map((s) => (
        <AddressRow key={s.id} s={s} customerId={customer.id} canEdit={canEdit} />
      ))}
    </ul>
  );
}

function AddressRow({ s, customerId, canEdit }: { s: Subscription; customerId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const update = useUpdateSubscription(customerId);
  const Icon = CHANNEL_ICON[s.channel];
  const device = s.device
    ? [s.device.platform, s.device.model, s.device.app_version && `v${s.device.app_version}`]
        .filter(Boolean)
        .join(" · ")
    : "";
  const next = s.status === "active" ? "unsubscribed" : "active";

  return (
    <li className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-default text-muted">
          <Icon className="size-4" />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <ChannelChip channel={s.channel} />
            <StateChip
              ok={s.status === "active" ? true : s.status === "invalid" ? "warn" : false}
              label={t(`customers.subscriptionStatus.${s.status}`)}
            />
            {s.is_primary ? (
              <Chip size="sm" variant="soft" color="accent">
                {t("customers.detail.primary")}
              </Chip>
            ) : null}
          </div>
          <span className="truncate font-mono text-[13px]" title={s.address}>
            {s.address}
          </span>
          <span className="text-xs text-muted">
            {[
              device,
              s.credential_alias,
              s.last_seen_at && t("customers.detail.lastSeen", { when: fmt.relative(s.last_seen_at) }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {s.invalid_reason ? (
            <span className="text-xs text-warning-soft-foreground">
              {t("issues.address_invalid.label")}: <Code>{s.invalid_reason}</Code>
            </span>
          ) : null}
        </div>
      </div>
      {canEdit ? (
        <Button
          size="sm"
          variant={next === "active" ? "secondary" : "ghost"}
          className="shrink-0 self-start sm:self-center"
          isPending={update.isPending}
          onPress={() =>
            update.mutate(
              { id: s.id, status: next },
              { onSuccess: () => toast.success(t("customers.detail.subscriptionUpdated")) },
            )
          }
        >
          {next === "active" ? t("customers.detail.reactivate") : t("customers.detail.unsubscribe")}
        </Button>
      ) : null}
    </li>
  );
}

function History({ customerId, onOpen }: { customerId: string; onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const log = useDeliveryLog({ customer_id: customerId });
  const rows = log.data?.pages.flatMap((p) => p.items) ?? [];
  if (log.isPending) return <LoadingBlock />;
  if (log.isError) return <ErrorBlock error={log.error} onRetry={() => log.refetch()} />;
  return (
    <div className="-mx-6 -mb-6 flex flex-col gap-4">
      <DeliveryTable
        rows={rows}
        label={t("customers.detail.history")}
        onOpen={onOpen}
        showRecipient={false}
        empty={t("customers.detail.historyEmpty")}
      />
      {log.hasNextPage ? (
        <div className="flex justify-center pb-6">
          <Button variant="secondary" isPending={log.isFetchingNextPage} onPress={() => log.fetchNextPage()}>
            {t("common.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Profile({ c }: { c: AdminCustomer }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const location = [c.location.city, c.location.region, c.location.country].filter(Boolean).join(", ");
  const attributes = Object.entries(c.attributes).filter(([k]) => k !== "name");
  const tags = Object.entries(c.tags);
  return (
    <div className="flex flex-col gap-5">
      <KeyValue
        items={[
          { label: t("customers.detail.externalId"), value: <Code>{c.external_id}</Code> },
          { label: t("customers.columns.app"), value: c.app.name },
          {
            label: t("customers.detail.location"),
            value: location ? (
              <span className="flex flex-col">
                {location}
                {c.location.source ? (
                  <span className="text-xs text-muted">
                    {t(`customers.locationSource.${c.location.source}`)}
                  </span>
                ) : null}
              </span>
            ) : (
              "—"
            ),
          },
          { label: t("customers.detail.language"), value: c.language ?? "—" },
          { label: t("customers.detail.timezone"), value: c.timezone ?? "—" },
          { label: t("customers.detail.lastActive"), value: fmt.dateTime(c.last_active_at) },
          { label: t("customers.detail.registered"), value: fmt.dateTime(c.created_at) },
        ]}
      />
      {attributes.length ? (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-muted">{t("customers.detail.attributes")}</span>
          <div className="flex flex-wrap gap-1.5">
            {attributes.map(([k, v]) => (
              <Chip key={k} size="sm" variant="secondary">
                <span className="text-muted">{k}</span> {typeof v === "string" ? v : JSON.stringify(v)}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}
      {tags.length ? (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-muted">{t("customers.detail.tags")}</span>
          <div className="flex flex-wrap gap-1.5">
            {tags.map(([k, v]) => (
              <Chip key={k} size="sm" variant="soft" color="accent">
                {k}: {v}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EraseCustomer({ c }: { c: AdminCustomer }) {
  const { t } = useTranslation();
  const erase = useEraseCustomer();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <DangerZone title={t("customers.detail.dangerZone")} description={t("customers.detail.eraseHint")}>
      <Button variant="danger-soft" onPress={() => setOpen(true)}>
        <TrashBin />
        {t("customers.detail.erase")}
      </Button>
      <ConfirmDialog
        isOpen={open}
        onOpenChange={setOpen}
        title={t("customers.detail.eraseTitle")}
        body={t("customers.detail.eraseBody")}
        confirmLabel={t("customers.detail.erase")}
        isPending={erase.isPending}
        error={erase.error}
        onConfirm={() =>
          erase.mutate(c.id, {
            onSuccess: () => {
              setOpen(false);
              toast.success(t("customers.detail.erased"));
              navigate("/customers", { replace: true });
            },
          })
        }
      />
    </DangerZone>
  );
}
